import googleAnalyticsV3 from "@analytics/google-analytics-v3";
import segmentPlugin from "@analytics/segment";
import Analytics from "analytics";
import type {
	SalesDrilldownMetric,
	SalesSummaryBreakdown,
} from "types/ecommerce.types";
import { config } from "./config";
import { posthog } from "./posthog";
import type { QboConnectFailureReason } from "./qboConnect";

/** Which window carried the OAuth round trip. */
export type QboConnectVia = "popup" | "redirect";

// Initialize analytics with Segment plugin
//
// 🪤 Register the Segment plugin only when there is a write key.
// `@analytics/segment` throws `No segment writeKey` from its `initialize`
// hook, and `initialize` is dispatched on the FIRST event rather than at
// import — so with `REACT_APP_SEGMENT_WRITE_KEY` unset, every page that fires
// an event on mount threw out of the dispatch chain (`/connection/qbo` fires
// three). Without a key Segment was never going to deliver anything anyway, so
// leaving the plugin out costs nothing. GA and the PostHog fan-out below are
// unaffected either way.
export const analytics = Analytics({
	app: "equility",
	plugins: [
		...(config.segment.writeKey
			? [
					segmentPlugin({
						writeKey: config.segment.writeKey,
					}),
				]
			: []),
		googleAnalyticsV3({
			trackingId: config.ga.id,
		}),
	],
});

// Handle analytics errors gracefully (e.g., when blocked by ad blockers)
analytics.on("error", (error) => {
	if (process.env.NODE_ENV === "development") {
		console.warn("Analytics error (likely blocked by ad blocker):", error);
	}
});

// ---------------------------------------------------------------------------
// Activation funnel identity
//
// The activation events below have to be joinable per user and per company, but
// several of them fire from places that cannot read a React hook: a popup
// helper, a store setter, an auth gate rendered before the app shell mounts. So
// identity is held here, written once at the auth boundary, and merged into
// every activation event.
//
// `identify()` already puts `userId` on the Segment/GA envelope, so the
// explicit property is redundancy on purpose: it survives a destination that
// drops the envelope id, and it keeps a raw event export self-describing.
//
// 🪤 This merge is deliberately NOT applied to the event helpers that existed
// before the activation work. Analytics has history against those payloads and
// widening them silently would change what a `category`-only event means. New
// events opt in by going through `trackActivation`; old ones keep calling
// `analytics.track` directly.
//
// PostHog receives these through the `track` listener below, same as every
// other `analytics.track` call.
// ---------------------------------------------------------------------------

interface ActivationIdentity {
	userId?: string;
	companyId?: number;
}

let identity: ActivationIdentity = {};

/**
 * Record who is acting, so later activation events can be joined to them.
 *
 * Called twice per session on purpose. The auth store knows the Firebase uid
 * the moment sign-in resolves — early enough for the attribution, email
 * verification and onboarding gates, which all run before the app shell and its
 * `user` fetch exist. `useUser` then upgrades it to the numeric app user id,
 * which is what every other system keys on.
 *
 * 🪤 `undefined` and `null` are NOT the same here. An omitted field is "leave
 * what you have" — that is what lets the uid→numeric-id upgrade above pass
 * `userId` alone without wiping the company. An explicit `null` CLEARS the
 * field, which the company store needs when the user leaves a company
 * (`CompanyMenu` and company settings both call `setCompany(null)`). Ignoring
 * the null instead would leave the previous company on every subsequent event
 * — a wrong attribution, which is worse than a missing one.
 */
export function setAnalyticsIdentity(next: {
	userId?: string | number | null;
	companyId?: number | null;
}) {
	if (next.userId !== undefined) {
		if (next.userId === null) delete identity.userId;
		else identity.userId = String(next.userId);
	}
	if (next.companyId !== undefined) {
		if (next.companyId === null) delete identity.companyId;
		else identity.companyId = next.companyId;
	}
}

/**
 * Reconciliation ids that have already reported `reconciliation_completed`.
 *
 * The activation rate is a count of this event, so it has to be exactly one per
 * completed run. Nothing upstream guarantees that:
 *
 *   - `useCompleteReconciliation` clears `isCompletingReconciliation` in its
 *     `finally`, i.e. the moment the PATCH resolves. The Complete button's only
 *     other guard is `status === 2`, which arrives with the refetch that
 *     `handleCompleteReconciliation` kicks off AFTER this event. Between those
 *     two the button is live again, so a double click is a second emit.
 *   - the API does not refuse the repeat. `PATCH /reconciliations/:id/complete`
 *     has no already-completed short circuit — `assertMutable` locks on review
 *     state (PENDING_REVIEW / SIGNED_OFF), not on status — so the second call
 *     returns 200 and the handler runs to the end.
 *
 * Deduping here rather than disabling the button keeps the fix to the metric:
 * the button deliberately stays clickable in states where it looks refusable
 * (EQ-2300), and narrowing that would change product behaviour to protect a
 * number.
 *
 * Session-scoped on purpose. It is cleared with the identity, because both are
 * "this tab, this user" state — see `resetAnalyticsIdentity`.
 */
const reportedCompletions = new Set<string>();

/** Drop the identity on logout so the next user in this tab is not conflated. */
export function resetAnalyticsIdentity() {
	identity = {};
	// The completion guard is per-user too: the next person in this tab must be
	// able to report their own runs, and ids are not shared between them.
	reportedCompletions.clear();
}

/**
 * `identify()` the user to Segment/GA so events join across sessions.
 *
 * Traits stay deliberately thin. PostHog already carries the richer profile
 * (`utils/posthog.ts`), and anything sent here is duplicated into every
 * downstream destination Segment fans out to.
 */
export function identifyAnalyticsUser(
	userId: string | number,
	traits?: Record<string, unknown>,
) {
	setAnalyticsIdentity({ userId });
	analytics.identify(String(userId), traits ?? {});
}

/**
 * Emit an activation-funnel event with the current identity attached.
 *
 * Properties the caller passes win over the stored identity, so a page that
 * knows a more specific company than the one last selected can say so.
 *
 * Goes to Segment/GA4, and to PostHog via the `track` listener below.
 *
 * 🪤 Never put a balance, an amount, an account number or a transaction
 * description in `props`. These events fan out to third parties. Counts,
 * durations, ids and closed enums only.
 */
export function trackActivation(
	event: string,
	props: Record<string, unknown> = {},
) {
	const merged: Record<string, unknown> = { ...identity, ...props };
	// An explicit `undefined` from a caller reads as "no value", not as a
	// property to ship — Segment would otherwise forward the key as null.
	for (const key of Object.keys(merged)) {
		if (merged[key] === undefined) delete merged[key];
	}
	analytics.track(event, merged);
}

/**
 * Event names equility-api already captures into PostHog itself.
 *
 * 🪤 A DENYLIST, never an allowlist. The mirror below forwards everything else
 * untouched, so a new `trackEvents` entry still reaches PostHog for free — the
 * property the mirror exists for. Only add a name here once the server emits
 * that exact name to PostHog, and say where.
 *
 * Empty today, and that is a position rather than an oversight.
 *
 * `reconciliation_completed` lived here from EQU-634 until EQU-899, because
 * equility-api captured that exact name on `AppEvents.reconciliationCompleted`
 * and PostHog counted one completion twice (2 of 4 people in a 30-day window
 * carried both emissions, inflating the activation numerator). EQU-868 then
 * renamed the server's PostHog event to `reconciliation_run_processed`, because
 * the server fires on every background processing pass rather than on a human
 * finishing a review — 83% of the production "completed" metric was a
 * background pass. Nothing server-side emits `reconciliation_completed` to
 * PostHog any more (Loops still gets it under the old name, a separate sink),
 * so leaving the entry here left the name with NO PostHog emitter at all: the
 * server had renamed away from it and the mirror below still skipped the
 * browser copy. The browser is the single emitter again.
 */
const SERVER_EMITTED_IN_POSTHOG: ReadonlySet<string> = new Set<string>();

/**
 * Mirror every named event in `trackEvents` into PostHog.
 *
 * PostHog autocapture is off now (it shipped the on-screen text of a client's
 * ledger). Autocapture was also the ONLY thing PostHog received about the
 * funnel: `signUp`, `reconciliation_start`, `plaid_connect` and the rest below
 * are dispatched through this `analytics` instance, which fans out to Segment
 * and GA — never to PostHog. Turning autocapture off without this listener
 * would leave the funnel baseline with pageviews and nothing else.
 *
 * 🪤 One listener on the core `track` action, NOT a posthog.capture() added to
 * each of the ~20 call sites below. Same events, a tenth of the diff, and —
 * the part that matters — an event added to `trackEvents` later arrives in
 * PostHog automatically instead of silently going to Segment only. Event names
 * and properties are passed through unchanged so the two systems stay
 * comparable.
 *
 * 🪤 This forwards every `analytics.track` call except the handful of names in
 * `SERVER_EMITTED_IN_POSTHOG` — it does not consult a declared allowlist. That
 * is what makes a new `trackEvents` entry arrive in PostHog for free, and it is
 * also why an event name emitted here must not ALSO be captured in
 * `utils/activationAnalytics.ts` or by equility-api: it would land twice with
 * nothing erroring. Nothing here reads the DOM, so no ledger text can ride
 * along.
 *
 * 🪤 The skip is PostHog-only and deliberately narrow. Segment and GA4 still
 * receive every event, including the skipped names, because `analytics.track`
 * has already fanned out by the time this listener runs. A skipped name is a
 * statement about who owns it in PostHog, NOT a statement that the event is
 * unwanted — do not "finish the job" by deleting its `trackActivation` call.
 */
analytics.on("track", ({ payload }) => {
	try {
		// Skipping here rather than at the call site is what keeps Segment/GA4
		// whole: a browser event usually carries properties its server twin does
		// not, so dropping the `trackActivation` call to settle PostHog ownership
		// would lose them from the other destinations too.
		if (SERVER_EMITTED_IN_POSTHOG.has(payload.event)) return;
		posthog.capture(payload.event, payload.properties);
	} catch {
		// Analytics must never break a user flow. posthog-js no-ops when no API
		// key is configured; anything else that throws is still not a reason to
		// fail a sign-up.
	}
});

// Event tracking functions organized by category
export const trackEvents = {
	user: {
		signIn: () => {
			analytics.track("signIn", {
				category: "user",
			});
		},
		signInGoogle: () => {
			analytics.track("signInGoogle", {
				category: "user",
			});
		},
		signUp: () => {
			analytics.track("signUp", {
				category: "user",
			});
		},
		forgotPassword: () => {
			analytics.track("forgotPassword", {
				category: "user",
			});
		},
		signUpGoogle: () => {
			analytics.track("signUpGoogle", {
				category: "user",
			});
		},
	},
	reconciliation: {
		// `companyId` and `dataSource` let the funnel's last step be segmented the
		// same way as `recon_wizard_opened`. `dataSource` is the wizard's closed
		// source enum, never ledger data.
		start: (props?: { companyId: number; dataSource: string }) => {
			analytics.track("reconciliation_start", {
				category: "reconciliation",
				...props,
			});
		},
		error: () => {
			analytics.track("reconciliation_error", {
				category: "reconciliation",
			});
		},
		/**
		 * A reconciliation reached a finished state. The activation metric.
		 *
		 * Supersedes EQU-20's payload-free `complete()` / `reconciliation_complete`
		 * (#558). Both answered "the run finished"; only this one carries the
		 * company and the counts the activation funnel is measured on, so EQU-20's
		 * variant was removed here rather than kept alongside — two names one
		 * letter apart, firing from the same `try` block, would have doubled the
		 * activation rate for anyone who summed them.
		 *
		 * `durationMs` is the time the user spent on this reconciliation's results
		 * view before completing it — NOT server-side processing time and not time
		 * since the run was created. The client never sees a start timestamp, and
		 * inventing one from `createdAt` would quietly count the days a half-done
		 * reconciliation sat open.
		 *
		 * `matchedCount` and `exceptionCount` are counts of transactions, never
		 * amounts. `dataSource` is the API's source-type enum.
		 *
		 * 🪤 At most ONE event per `reconciliationId` per session — see
		 * `reportedCompletions` above. Calling this twice for the same run is a
		 * no-op, not a second event.
		 *
		 * 🪤 This call is the ONLY emitter of `reconciliation_completed` in
		 * PostHog, as well as in Segment/GA4. equility-api owned the PostHog copy
		 * between EQU-634 and EQU-868; it now emits
		 * `reconciliation_run_processed` for a processing pass and
		 * `first_reconciliation_completed` for an account's first one, so this
		 * name is the browser's again (EQU-899). Two consequences worth knowing
		 * before you read a PostHog number:
		 *
		 *   - this event means a PERSON clicked Complete. The server's
		 *     `reconciliation_run_processed` means a backend pass finished, which
		 *     is a different and much larger number.
		 *   - it is a browser event, so an ad blocker or a locked-down firm
		 *     machine can eat it. Counts here run low, never high. Do not add a
		 *     second server emitter under this name to "fix" that — see
		 *     `SERVER_EMITTED_IN_POSTHOG`.
		 */
		completed: (props: {
			companyId: number;
			accountId?: string;
			reconciliationId: string;
			matchedCount: number;
			exceptionCount: number;
			durationMs?: number;
			dataSource?: string;
		}) => {
			if (reportedCompletions.has(props.reconciliationId)) return;
			reportedCompletions.add(props.reconciliationId);
			trackActivation("reconciliation_completed", {
				category: "reconciliation",
				...props,
			});
		},
	},
	// The New Reconciliation wizard — the last gate before a trial user sees a
	// match, and the one where an abandonment is most expensive, because signup,
	// email verification and the QuickBooks OAuth grant are already paid for
	// (EQU-17). These three answer one question: did people leave because of the
	// FIELDS, or because of what comes after them?
	//
	// `stepIndex` is zero-based and matches the modal's own `step` state: 0 is
	// "Account & Date Range", 1 is "Data Source & Balances".
	//
	// 🪤 Counts and closed enums only — never a balance, an account number or a
	// period. The prefill properties report WHETHER a default was in the field,
	// never what it was. That is the question the prefill work needs answered,
	// and it is the only form of the answer that is safe to send to a third
	// party.
	reconWizard: {
		/**
		 * The wizard was opened.
		 *
		 * 🪤 Deliberately carries no prefill properties. Both prefills are written
		 * by effects that run AFTER the render this fires on, so anything read
		 * here would report "nothing was offered" for every session including the
		 * ones that were prefilled. The prefill state is reported on
		 * `stepAdvanced` instead, where the fields have settled and the user has
		 * actually seen them.
		 */
		opened: (props: { mode: "create" | "edit"; accountCount: number }) => {
			trackActivation("recon_wizard_opened", {
				category: "reconWizard",
				...props,
			});
		},
		/**
		 * The user got past a step.
		 *
		 * `onOfferedPeriod` is whether the dates they advanced with are still the
		 * ones the wizard offered, and `openingBalanceFromQuickBooks` whether the
		 * Beginning Balance is still QuickBooks' figure. Read together with
		 * `abandoned`, those two answer whether confirming defaults is what people
		 * actually do, or whether they replace them and the prefill bought us
		 * nothing.
		 */
		stepAdvanced: (props: {
			stepIndex: number;
			mode: "create" | "edit";
			onOfferedPeriod: boolean;
			openingBalanceFromQuickBooks: boolean;
		}) => {
			trackActivation("recon_wizard_step_advanced", {
				category: "reconWizard",
				...props,
			});
		},
		/**
		 * The wizard was closed without starting or saving a reconciliation.
		 *
		 * `stepIndex` is the step it was abandoned ON, which is the property the
		 * funnel is read by: step 0 means the account and dates stopped them, step
		 * 1 means the data source or the balances did.
		 *
		 * `via` separates "left on purpose" from "pressed Escape", because the
		 * modal only became escapable in EQU-17 — a rise in `escape` is that
		 * change working, not a new leak, and without this property the two are
		 * indistinguishable in the funnel.
		 */
		abandoned: (props: {
			stepIndex: number;
			mode: "create" | "edit";
			via: "escape" | "mask" | "close";
			dirty: boolean;
		}) => {
			trackActivation("recon_wizard_abandoned", {
				category: "reconWizard",
				...props,
			});
		},
	},
	/**
	 * EQU-921. The E-Commerce Sales Summary drill-down.
	 *
	 * UI behaviour only, so the client owns both names: the server cannot see
	 * that a row was clicked, and the mismatch is a comparison between two
	 * figures that only ever meet in the browser.
	 *
	 * 🪤 Deliberately carries NO money. Product's spec asked for the card and
	 * drill-down amounts on `sales_summary_drilldown_mismatch`; these events
	 * fan out to Segment and GA4, and a store's gross sales is a customer's
	 * financial data. `direction` and `orderCount` answer the same question
	 * ("how often, and which way") without shipping the figures.
	 */
	salesSummary: {
		/**
		 * A Sales Summary line was opened. One event per click. `row` is the
		 * line's own id, so a subtotal (EQU-1026: `net_sales`, `total_sales`)
		 * is told apart from an order-level line without a second event name.
		 */
		rowClicked: (props: { row: SalesSummaryBreakdown }) => {
			trackActivation("sales_summary_row_clicked", {
				category: "salesSummary",
				...props,
			});
		},
		/**
		 * The breakdown's own total did not equal the card's figure, and the
		 * warning was shown. Fires on the outcome: the banner being on screen,
		 * not the read starting.
		 */
		drilldownMismatch: (props: {
			row: SalesDrilldownMetric;
			direction: "breakdown_higher" | "breakdown_lower";
			orderCount: number;
		}) => {
			trackActivation("sales_summary_drilldown_mismatch", {
				category: "salesSummary",
				...props,
			});
		},
	},
	plaid: {
		connect: () => {
			analytics.track("plaid_connect", {
				category: "plaid",
			});
		},
		error: () => {
			analytics.track("plaid_error", {
				category: "plaid",
			});
		},
	},
	// "How did you find Equility?" has NO helper here. Its answer is
	// `signup_attribution_submitted`, captured inside `submitAttribution`
	// (`hooks/useOnboarding.ts`). An `attribution_submitted` helper lived here
	// alongside two other emitters for the same click, so one answer produced
	// three differently-named events (EQU-401). Nothing errored — the only
	// symptom was a gate whose pass-through read 3x.
	//
	// 🪤 Re-adding a helper here is enough on its own to break it again: the
	// `analytics.on("track")` mirror above forwards every name in `trackEvents`
	// to PostHog without an allowlist, so a second declaration never has to be
	// wired to a call site to start double-counting.
	// The /verify-email gate. The delay between `sent` and `verified` is read off
	// the two events' own timestamps in the destination.
	//
	// 🪤 `sent` only covers a RESEND. The first verification email is sent
	// server-side during signup, so a user who verifies on the first link
	// produces `email_verified` with no client-side `email_verification_sent`
	// before it. Measuring that first delay needs the API-side event.
	emailVerification: {
		sent: (trigger: "resend" | "resend_expired_link") => {
			trackActivation("email_verification_sent", {
				category: "emailVerification",
				trigger,
			});
		},
		verified: (via: "action_link" | "detected") => {
			trackActivation("email_verified", {
				category: "emailVerification",
				via,
			});
		},
	},
	// The five-slide product tour. `stepIndex` is zero-based.
	onboarding: {
		stepViewed: (stepIndex: number, stepTitle: string, stepCount: number) => {
			trackActivation("onboarding_step_viewed", {
				category: "onboarding",
				stepIndex,
				stepTitle,
				stepCount,
			});
		},
		skipped: (stepIndex: number, stepCount: number) => {
			trackActivation("onboarding_skipped", {
				category: "onboarding",
				stepIndex,
				stepCount,
			});
		},
		/**
		 * The onboarding gate was passed — by reaching the end OR by skipping.
		 *
		 * Both paths complete it server-side, so both emit this: it is the funnel
		 * step "left onboarding and entered the app". `completedVia` keeps the
		 * narrower reading available, so neither "how many finished the tour" nor
		 * "how many got through the gate" needs a second event to answer.
		 */
		completed: (completedVia: "finished" | "skipped", stepCount: number) => {
			trackActivation("onboarding_completed", {
				category: "onboarding",
				completedVia,
				stepCount,
			});
		},
	},
	// The QuickBooks connect gate. Every trial user passes through it and it
	// emitted nothing until now, so the size of the drop-off here was unknown.
	// `via` separates the popup flow from the same-tab redirect fallback, and
	// `recovered` marks a failure we handled without the user losing the thread
	// — so a blocked popup that redirects successfully doesn't read as a loss.
	qbo: {
		connectStarted: (via: QboConnectVia) => {
			analytics.track("qbo_connect_started", {
				category: "qbo",
				via,
			});
		},
		connectSucceeded: (via: QboConnectVia) => {
			analytics.track("qbo_connect_succeeded", {
				category: "qbo",
				via,
			});
		},
		connectFailed: (reason: QboConnectFailureReason, recovered = false) => {
			analytics.track("qbo_connect_failed", {
				category: "qbo",
				reason,
				recovered,
			});
		},
	},
	// Intuit App Store review prompt, shown after a completed reconciliation or
	// a signed-off close. `trigger` says which, so the two can be compared;
	// `matchedCount` rides along on the reconciliation path so we can see
	// whether the size of the win predicts a click-through.
	//
	// `skipped` is the denominator the funnel was missing: without it, a prompt
	// that never appeared because the account is under Intuit's 90-day bar is
	// indistinguishable from one the user snoozed, and from the feature being
	// broken. `reason` is a `ReviewPromptSkipReason`.
	reviewPrompt: {
		shown: (trigger: string, matchedCount?: number) => {
			analytics.track("review_prompt_shown", {
				category: "reviewPrompt",
				trigger,
				matchedCount,
			});
		},
		reviewClicked: (trigger: string, matchedCount?: number) => {
			analytics.track("review_prompt_review_clicked", {
				category: "reviewPrompt",
				trigger,
				matchedCount,
			});
		},
		remindLater: (trigger: string, matchedCount?: number) => {
			analytics.track("review_prompt_remind_later", {
				category: "reviewPrompt",
				trigger,
				matchedCount,
			});
		},
		alreadyReviewed: (trigger: string, matchedCount?: number) => {
			analytics.track("review_prompt_already_reviewed", {
				category: "reviewPrompt",
				trigger,
				matchedCount,
			});
		},
		skipped: (trigger: string, reason: string) => {
			analytics.track("review_prompt_skipped", {
				category: "reviewPrompt",
				trigger,
				reason,
			});
		},
	},
	reviewReward: {
		linkClicked: (platform: string) => {
			analytics.track("review_reward_link_clicked", {
				category: "reviewReward",
				platform,
			});
		},
		claimed: (platform: string, credits: number, created: boolean) => {
			analytics.track("review_reward_claimed", {
				category: "reviewReward",
				platform,
				credits,
				created,
			});
		},
	},
};

// Usage example:
// trackEvents.user.signIn()
// trackEvents.compliance.start()
// trackEvents.reconciliation.limit()
// trackEvents.plaid.connect()
